// Puente liviano entre quien abre el simulador (la herramienta del encabezado, /portal/simulacion) y el panel, que llega
// aparte (lazy). Si el panel todavía no está montado, el pedido queda en espera y el panel lo toma al montarse.
import type { SimMode } from "./simulatorModel";

/** Lo que pide quien abre: nada (retomar lo de la pestaña) o un objetivo y, si viene de un enlace, su conversación. */
export type OpenRequest = {
  mode?: SimMode;
  employeeId?: number | null;
  channelId?: number | null;
  contactId?: number | null;
  sessionId?: number | null;
};

type Listener = (request: OpenRequest | null, toggle?: boolean) => void;

/** Estado que muestra la herramienta del encabezado: abierta, minimizada (con sus novedades) o cerrada. */
export type SimulatorShellState = { open: boolean; minimized: boolean; activity: number };

let listener: Listener | null = null;
let pending: { request: OpenRequest | null } | null = null;
const stateListeners = new Set<() => void>();
let state: SimulatorShellState = { open: false, minimized: false, activity: 0 };

/** Abre el simulador (o lo trae al frente si estaba minimizado). */
export function openSimulator(request: OpenRequest | null = null): void {
  if (listener) listener(request);
  else pending = { request };
}

/** La herramienta del encabezado: abre o, con la ventana a la vista, la minimiza. */
export function toggleSimulator(): void {
  if (listener) listener(null, true);
  else pending = { request: null };
}

/** El panel se registra al montarse y recibe el pedido que quedó en espera. */
export function registerSimulator(next: Listener): () => void {
  listener = next;
  if (pending) {
    const waiting = pending;
    pending = null;
    next(waiting.request);
  }
  return () => {
    if (listener === next) listener = null;
  };
}

/**
 * Lo que la herramienta del encabezado dibuja: presionada con la ventana a la vista; minimizada, con el número de
 * novedades (el simulador no agrega otro botón flotante: el único del catálogo es el del chat, C27).
 */
export function simulatorState(): SimulatorShellState {
  return state;
}

export function setSimulatorState(next: SimulatorShellState): void {
  if (state.open === next.open && state.minimized === next.minimized && state.activity === next.activity) return;
  state = next;
  for (const notify of stateListeners) notify();
}

export function subscribeSimulator(notify: () => void): () => void {
  stateListeners.add(notify);
  return () => stateListeners.delete(notify);
}

/** Lee la dirección /portal/simulacion?mode=…&employee=…&channel=…&contact=…&session=… (enlaces del portal actual). */
export function requestFromQuery(search: string): OpenRequest | null {
  const query = new URLSearchParams(search);
  const id = (name: string) => {
    const value = Number(query.get(name));
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  };
  const employeeId = id("employee");
  const channelId = id("channel");
  const contactId = id("contact");
  const sessionId = id("session");
  const raw = query.get("mode");
  if (!raw && !employeeId && !channelId && !contactId && !sessionId) return null;
  const mode: SimMode =
    raw === "DIRECT_EMPLOYEE" || raw === "CHANNEL_SIMULATION"
      ? raw
      : employeeId
        ? "DIRECT_EMPLOYEE"
        : "CHANNEL_SIMULATION";
  return { mode, employeeId, channelId, contactId, sessionId };
}
